import { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import InputRange from 'react-input-range';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import CurrencyFormatter, { FormatCurrency } from '../CurrencyFormatter';
import { ResponsiveContext } from '../../Providers/Responsive';

// const minValue = 0;
// const maxInput = 9999;
// styling handled on the global style style sheet

const InputSlider = ({
  state,
  stateKey,
  updateState = null,
  label,
  minValue = 0,
  maxValue = 2000,
  maxInput = 9999,
  step = 10,
  color = 'red',
}) => {
  let timeOut;
  const intl = useIntl();
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [input, setInput] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [float, setFloat] = useState(false);

  const { mobile } = useContext(ResponsiveContext);

  const classNames = {
    inputRange: 'inputRange',
    label: 'label',
    labelContainer: 'labelContainer',
    maxLabel: 'maxLabel',
    minLabel: 'minLabel',
    track: 'track',
    activeTrack: 'activeTrack',
    disabledInputRange: 'disabledInputRange',
    slider: `slider ${label}`,
    sliderContainer: 'sliderContainer',
    valueLabel: 'valueLabel',
  };

  const showToolTip = () => {
    setIsOpen(true);
    if (window !== 'undefined') {
      window.clearTimeout(timeOut);
      timeOut = window.setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    }
  };

  const showFloatingToolTip = () => {
    setFloat(true);
    setIsOpen(true);
  };

  const focusToolTip = () => {
    setIsOpen(true);
  };

  const hideToolTip = () => {
    setIsOpen(false);
  };

  const updateInput = value => {
    if (value === maxValue) showToolTip();
    updateState(stateKey, value);
  };

  const blur = e => {
    const value = e.target.value >= 1 ? e.target.value : 0;
    updateState(stateKey, parseInt(value, 10));
    setInput(false);
  };

  useEffect(() => {
    ref.current.focus();
    return () => {
      window.clearTimeout(timeOut);
    };
  }, [input]);

  const currency = FormatCurrency(maxValue, intl.locale);

  return (
    <div className="inputSliderContainer">
      <fieldset>
        <legend>
          <span className="sr-only">{`${intl.formatMessage({
            id: 'monthlyPurchases',
          })} ${label}`}</span>
        </legend>
        <div className={`inputSlider ${color}`}>
          <InputRange
            step={step}
            maxValue={maxValue}
            minValue={minValue}
            value={state}
            id={label}
            onChange={updateInput}
            classNames={classNames}
            ariaLabelledby={`${label.toLowerCase().split(' ').join('-')}-label`}
          />
          <label
            id={`${label.toLowerCase().split(' ').join('-')}-label`}
            className="slider-label"
            htmlFor={`${label.toLowerCase().split(' ').join('-')}-stepper`}
            aria-label={`${intl.formatMessage({
              id: 'monthlyPurchases',
            })} ${label}`}
          >
            {label}
          </label>
          <input
            id={`${label.toLowerCase().split(' ').join('-')}-stepper`}
            name={`${label.toLowerCase().split(' ').join('-')}-stepper`}
            type="number"
            className="sliderValueLabel"
            value={state}
            min={minValue}
            max={maxInput}
            ref={ref}
            onChange={e =>
              updateState(
                stateKey,
                parseInt(
                  e.target.value <= maxInput ? e.target.value : maxInput,
                  10,
                ),
              )
            }
            onBlur={blur}
            aria-label={`${intl.formatMessage({
              id: 'monthlyPurchases',
            })} ${label}`}
            style={{
              display: !input ? 'none' : 'block',
            }}
          />
          <button
            data-tooltip-id={`${label.toLowerCase().split(' ').join('-')}-btn`}
            type="button"
            className="sliderValueLabel"
            ref={inputRef}
            onClick={() => setInput(true)}
            onFocus={() => focusToolTip()}
            onMouseEnter={() => showFloatingToolTip()}
            onBlur={() => hideToolTip()}
            onMouseLeave={() => hideToolTip()}
            onTouchStart={() => setInput(true)}
            data-input-name={label}
            data-tooltip-content={`${intl.formatMessage({
              id: 'sliderTip',
            })} ${label} ${intl.formatMessage({
              id: 'sliderTipAbove',
            })} ${currency}.`}
            aria-label={`${intl.formatMessage({
              id: 'sliderTip',
            })} ${label} ${intl.formatMessage({
              id: 'sliderTipAbove',
            })} ${currency}.`}
            style={{
              display: input ? 'none' : 'block',
            }}
          >
            <CurrencyFormatter value={state} lang={intl.locale} />
          </button>
        </div>
      </fieldset>
      {!mobile && !float && (
        <ReactTooltip
          id={`${label.toLowerCase().split(' ').join('-')}-btn`}
          isOpen={isOpen}
          afterHide={() => setFloat(false)}
          className="tool-tip"
          place="left"
          multiline
        />
      )}
      {!mobile && float && (
        <ReactTooltip
          id={`${label.toLowerCase().split(' ').join('-')}-btn`}
          isOpen={isOpen}
          className="tool-tip"
          afterHide={() => setFloat(false)}
          float="true"
          place="left"
          multiline
        />
      )}
    </div>
  );
};

InputSlider.propTypes = {
  state: PropTypes.number.isRequired,
  stateKey: PropTypes.string.isRequired,
  updateState: PropTypes.func,
  label: PropTypes.string.isRequired,
  maxValue: PropTypes.number,
  minValue: PropTypes.number,
  maxInput: PropTypes.number,
  step: PropTypes.number,
  color: PropTypes.string,
};

export default InputSlider;
